import { gql } from '@apollo/client'
import classNames from 'classnames'
import { useContext } from 'react'

import { getCardsByFrequency } from '../../utils'
import { BoxLockContext } from '../context'
import { PaywallLock } from '../paywall-lock'
import {
  DynamicMultipleOffersSwitchFrequencyNewBenefitsLock_OfferLockDataFragment,
  DynamicMultipleOffersSwitchFrequencyNewBenefitsLock_ViewerFragment,
} from './__generated__/dynamic-multiple-offers-switch-frequency-new-benefits-lock.graphql'
import { DynamicMultipleOffersSwitchFrequencyLockContent, ModalLock } from './components'
import styles from './dynamic-multiple-offers-switch-frequency-new-benefits-lock.module.scss'

export interface DynamicMultipleOffersSwitchFrequencyNewBenefitsLockProps {
  offerLockData: DynamicMultipleOffersSwitchFrequencyNewBenefitsLock_OfferLockDataFragment
  viewer: DynamicMultipleOffersSwitchFrequencyNewBenefitsLock_ViewerFragment
}

export const DynamicMultipleOffersSwitchFrequencyNewBenefitsLock = ({
  offerLockData,
  viewer,
}: DynamicMultipleOffersSwitchFrequencyNewBenefitsLockProps) => {
  const { componentType } = useContext(BoxLockContext)
  const isPaywall = componentType === 'paywall'

  const { monthlyCards, yearlyCards } = getCardsByFrequency(offerLockData?.body?.cards)

  const content = (
    <DynamicMultipleOffersSwitchFrequencyLockContent
      offerLockData={offerLockData}
      viewer={viewer}
      isPaywall={isPaywall}
    />
  )

  const isOneOfferByFrequency = Math.max(monthlyCards.length, yearlyCards.length) === 1
  const isThreeOffersByFrequency = Math.max(monthlyCards.length, yearlyCards.length) === 3
  const modalContentClassName = classNames({
    [styles.modalContent]: isThreeOffersByFrequency,
  })

  switch (componentType) {
    case 'paywall':
      return <PaywallLock offerLockData={offerLockData}>{content}</PaywallLock>
    case 'modal':
      return (
        <ModalLock
          offerLockData={offerLockData}
          size={isOneOfferByFrequency ? 'sm' : 'lg'}
          modalContentClassName={modalContentClassName}
        >
          {content}
        </ModalLock>
      )
    default:
      return null
  }
}

DynamicMultipleOffersSwitchFrequencyNewBenefitsLock.fragments = {
  offerLockData: gql`
    fragment DynamicMultipleOffersSwitchFrequencyNewBenefitsLock_offerLockData on OfferLockData {
      ...PaywallLock_offerLockData
      ...NewModalLock_offerLockData
      ...NewDynamicMultipleOffersSwitchFrequencyLockContent_offerLockData
    }
    ${PaywallLock.fragments.offerLockData}
    ${ModalLock.fragments.offerLockData}
    ${DynamicMultipleOffersSwitchFrequencyLockContent.fragments.offerLockData}
  `,
  viewer: gql`
    fragment DynamicMultipleOffersSwitchFrequencyNewBenefitsLock_viewer on Profile {
      ...NewDynamicMultipleOffersSwitchFrequencyLockContent_viewer
    }
    ${DynamicMultipleOffersSwitchFrequencyLockContent.fragments.viewer}
  `,
}
